import React from "react";
import WebProfitSpinner from "../system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import DomainVerificationRoundedIcon from "@mui/icons-material/DomainVerificationRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import { projectsApi } from "../../api/projects";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

function projectKey(project) {
  return project?.slug || project?.id || project?._id || "";
}

function clean(value) {
  return String(value || "").trim();
}

function domainRows(project) {
  return Array.isArray(project?.deployment?.customDomains)
    ? project.deployment.customDomains.filter((entry) => clean(entry?.name))
    : [];
}

export default function SharedStorefrontMigrationPanel({
  project,
  canManage = false,
  onProjectChange,
  compact = false,
}) {
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");

  const key = projectKey(project);
  const deployment = project?.deployment || {};
  const shared = clean(deployment.mode).toLowerCase() === "shared";
  const sharedServiceId = clean(deployment.serviceId);
  const managedUrl = clean(deployment.managedUrl);
  const legacyServiceId = clean(deployment.legacyServiceId);
  const legacyDeleted = Boolean(deployment.legacyDeletedAt);
  const domains = domainRows(project);
  const legacyDomains = legacyServiceId
    ? domains.filter((entry) => clean(entry?.serviceId) === legacyServiceId)
    : [];
  const sharedPendingDomains = shared
    ? domains.filter(
        (entry) =>
          clean(entry?.serviceId) !== sharedServiceId ||
          clean(entry?.status).toLowerCase() !== "live",
      )
    : domains;
  const canDeleteLegacy =
    shared &&
    Boolean(legacyServiceId) &&
    !legacyDeleted &&
    legacyDomains.length === 0 &&
    sharedPendingDomains.length === 0;

  const applyProject = React.useCallback(
    (nextProject) => {
      if (nextProject && typeof onProjectChange === "function") onProjectChange(nextProject);
      return nextProject;
    },
    [onProjectChange],
  );

  const run = React.useCallback(
    async (action, task) => {
      if (!key || busy) return;
      try {
        setBusy(action);
        setError("");
        setNotice("");
        const data = await task();
        applyProject(data?.project || data?.migration?.project || data?.removal?.project || null);
        setNotice(clean(data?.message) || "Operación completada.");
      } catch (requestError) {
        setError(requestError?.message || "No se pudo completar la migración de infraestructura.");
      } finally {
        setBusy("");
      }
    },
    [applyProject, busy, key],
  );

  if (!canManage || !project) return null;

  const migrateProject = () =>
    run("project", () => projectsApi.migrateToSharedStorefront(key));

  const migrateDomains = () => {
    const confirmed = window.confirm(
      "WebProfit moverá los dominios personalizados del Render legado al Storefront compartido. El Render viejo NO se eliminará. Si Render rechaza el alta del nuevo vínculo, WebProfit intentará restaurar el dominio en el servicio anterior. ¿Continuar?",
    );
    if (!confirmed) return;
    run("domains", () => projectsApi.migrateDomainsToSharedStorefront(key));
  };

  const deleteLegacy = () => {
    const confirmed = window.confirm(
      "Esta acción elimina definitivamente el servicio Render legado. Sólo continuá si los dominios ya figuran activos en el Storefront compartido. ¿Eliminar Render legado?",
    );
    if (!confirmed) return;
    run("delete", () => projectsApi.deleteLegacyRender(key));
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: compact ? 1.5 : 2,
        borderRadius: compact ? 3 : 4,
        borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
        bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
      }}
    >
      <Stack spacing={1.35}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(243,90,58,.10)",
                color: BRAND.orange,
              }}
            >
              <SavingsRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.15 }}>
                Storefront compartido
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                Infraestructura · sólo superadministrador
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={shared ? "Compartido" : "Render legado"}
            color={shared ? "success" : "warning"}
            variant={shared ? "filled" : "outlined"}
            sx={{ fontWeight: 900 }}
          />
        </Stack>

        {!shared ? (
          <Alert severity="info">
            El primer paso cambia el proyecto al Storefront central y desactiva el auto-deploy del Render actual. Los dominios personalizados siguen asociados al servicio legado hasta que los muevas explícitamente.
          </Alert>
        ) : (
          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            <Chip size="small" label="Auto-deploy central" color="success" variant="outlined" sx={{ fontWeight: 850 }} />
            {legacyServiceId && !legacyDeleted ? (
              <Chip size="small" label="Legado sin auto-deploy" color="warning" variant="outlined" sx={{ fontWeight: 850 }} />
            ) : null}
            {legacyDeleted ? (
              <Chip size="small" label="Legado eliminado" color="success" sx={{ fontWeight: 850 }} />
            ) : null}
          </Stack>
        )}

        {shared && managedUrl ? (
          <Box
            sx={{
              px: 1.35,
              py: 1.1,
              borderRadius: 2.5,
              bgcolor: "rgba(1,142,66,.055)",
              border: "1px solid rgba(1,142,66,.14)",
            }}
          >
            <Typography variant="caption" sx={{ display: "block", fontWeight: 900, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)" }}>
              URL WebProfit automática
            </Typography>
            <Typography
              component="a"
              href={managedUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                mt: 0.2,
                color: BRAND.dark,
                fontWeight: 900,
                textDecoration: "none",
                overflowWrap: "anywhere",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {managedUrl}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
              Sale del slug del proyecto. No requiere crear un Render ni registrar este subdominio por cliente si el wildcard ya está configurado.
            </Typography>
          </Box>
        ) : shared ? (
          <Alert severity="warning">
            El Storefront compartido está activo, pero falta STOREFRONT_MANAGED_BASE_DOMAIN en el backend. Hasta configurarlo se seguirá usando la URL técnica de Render con ?project=slug.
          </Alert>
        ) : null}

        {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
        {notice ? <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert> : null}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} flexWrap="wrap" useFlexGap>
          {!shared ? (
            <Button
              type="button"
              variant="contained"
              startIcon={busy === "project" ? <WebProfitSpinner size={16} color="inherit" /> : <CloudSyncRoundedIcon />}
              disabled={Boolean(busy)}
              onClick={migrateProject}
              sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, borderRadius: 999 }}
            >
              Migrar al Storefront compartido
            </Button>
          ) : null}

          {shared && legacyServiceId && !legacyDeleted && legacyDomains.length > 0 ? (
            <Button
              type="button"
              variant="outlined"
              startIcon={busy === "domains" ? <WebProfitSpinner size={16} /> : <DomainVerificationRoundedIcon />}
              disabled={Boolean(busy)}
              onClick={migrateDomains}
              sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
            >
              Mover {legacyDomains.length} dominio{legacyDomains.length === 1 ? "" : "s"} al Storefront
            </Button>
          ) : null}

          {shared && legacyServiceId && !legacyDeleted ? (
            <Button
              type="button"
              color="error"
              variant="outlined"
              startIcon={busy === "delete" ? <WebProfitSpinner size={16} /> : <DeleteForeverRoundedIcon />}
              disabled={Boolean(busy) || !canDeleteLegacy}
              onClick={deleteLegacy}
              sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
            >
              Eliminar Render legado
            </Button>
          ) : null}
        </Stack>

        {shared && legacyServiceId && !legacyDeleted ? (
          <>
            <Divider />
            <Stack spacing={0.35}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                Estado antes de borrar el legado
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                {legacyDomains.length > 0
                  ? `${legacyDomains.length} dominio(s) todavía dependen del Render anterior.`
                  : sharedPendingDomains.length > 0
                    ? `${sharedPendingDomains.length} dominio(s) ya fueron movidos pero todavía no figuran live. Verificá DNS/HTTPS antes de borrar.`
                    : "Todos los dominios están live en el Storefront compartido. Ya se puede eliminar el Render legado."}
              </Typography>
            </Stack>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}
